import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  inject,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { InputComponent } from 'src/app/components/shared/input';
import { CategoryService } from 'src/app/services/category';
import { LocaleService } from 'src/app/services/locale';
import { PlusComponent } from '../../../../icons/plus';
import { DropdownComponent } from '../../../../shared/dropdown';
import { TrashComponent } from '../../../../icons/trash';
import { CountryPickerComponent } from '../../../../shared/country-picker';
import { YearPickerComponent } from '../../../../shared/year-picker';
import { AngleDownComponent } from '../../../../icons/angle-down';
import { debounceTime, distinctUntilChanged } from 'rxjs';

interface Subcategory {
  id: string;
  label: string;
  itaLabel?: string; // obbligatoria
}

export interface MainCategory {
  name: string;
  itaName: string;
  subcategories: Subcategory[];
}

export interface Answer {
  main_category?: string;
  main_category_info?: {
    name: string;
    itaName: string;
  };
  purchase_category_info?: Subcategory;
  spend_region?: string;
  money_amount?: number;
  money_unit?: string;
  spend_year?: number;
  note?: string;
}
interface Question {
  answers: Answer[] | null;
  emissions: any[];
  id: string;
  done: boolean;
  title: string;
  heading: string;
  doubt: string;
}

interface Category {
  questions: Question[];
  id: string;
  done: boolean;
  title: string;
  heading: string;
  doubt: string;
  emissions: any[];
}

@Component({
  selector: 'app-category4-question2',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputComponent,
    PlusComponent,
    DropdownComponent,
    TrashComponent,
    CountryPickerComponent,
    YearPickerComponent,
    AngleDownComponent
  ],
  template: `
    <!-- Help Box -->
    <!-- Yes / No toggle -->
    <div class="flex flex-wrap gap-4 sm:gap-6 mt-4">
      <div
        (click)="toggleUsage(true)"
        class="cursor-pointer flex gap-2 items-center"
      >
        <div
          [class.bg-green-950]="usage"
          class="w-4 h-4 border rounded-full flex-shrink-0"
        ></div>
        <span>{{ strings[language].choice_yes }}</span>
      </div>
      <div
        (click)="toggleUsage(false)"
        class="cursor-pointer flex gap-2 items-center"
      >
        <div
          [class.bg-green-950]="!usage"
          class="w-4 h-4 border rounded-full flex-shrink-0"
        ></div>
        <span>{{ strings[language].choice_no }}</span>
      </div>
    </div>

    <!-- Purchase Table -->
    <div *ngIf="usage">
      <div class="flex justify-end mb-2 items-center">
        <div
          (click)="addRow()"
          class="bg-[#F8F9FA] px-3 py-2 rounded text-white text-md cursor-pointer"
        >
          <app-plus></app-plus>
        </div>
      </div>
      <form [formGroup]="form">
        <div class="space-y-4" formArrayName="purchases">
          <div
            *ngFor="let row of purchases.controls; let i = index"
            [formGroupName]="i"
            class="border rounded-lg p-4"
          >
            <div class="flex justify-end mb-4 cursor-pointer">
              <div
                class="w-1/12 flex justify-end items-center cursor-pointer"
                (click)="removeRow(i)"
              >
                <app-trash></app-trash>
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium mb-1">
                  {{ strings[language].lable_1 }}
                </label>

                <div class="relative" #closeDropdown>
                  <!-- Dropdown trigger -->
                  <div
                    class="p-3 pr-10 rounded-lg border w-full border-gray-300 bg-white cursor-pointer hover:border-[#043B22] focus-within:ring-2 focus-within:ring-[#043B22] h-12 flex items-center relative"
                    (click)="toggleDropdown(i)"
                    [title]="getSelectedLabel(row)"
                  >
                    <span class="text-gray-800 block truncate w-full">
                      {{
                        getSelectedLabel(row) ||
                          strings[language].placeholder_label
                      }}
                    </span>
                    <app-angle-down
                      class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                    ></app-angle-down>
                  </div>

                  <!-- Dropdown content -->
                  <ul
                    *ngIf="dropdownOpenStates[i]"
                    class="absolute left-0 right-0 z-20 bg-white border border-gray-300 mt-2 max-h-64 overflow-auto shadow-xl rounded-lg scroll-smooth"
                  >
                    <!-- Search input -->
                    <li
                      class="sticky top-0 bg-white border-b border-gray-200 z-10 whitespace-nowrap"
                    >
                      <input
                        type="text"
                        [formControl]="categorySearchControl"
                        placeholder="{{ strings[language].lable_1 }}"
                        class="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#043B22]"
                        (click)="$event.stopPropagation()"
                      />
                    </li>

                    <!-- Categories and subcategories -->
                    <ng-container *ngFor="let category of filteredCategories">
                      <!-- Category title -->
                      <li
                        class="px-4 py-2 text-sm font-semibold text-[#043B22] bg-gray-100 sticky top-[42px] z-0"
                      >
                        {{
                          language === 'english'
                            ? category.name
                            : category.itaName
                        }}
                      </li>

                      <!-- Subcategory items -->
                      <li
                        *ngFor="let sub of category.subcategories"
                        class="px-4 py-2 text-sm text-gray-700 hover:bg-[#E6F2EC] hover:text-[#043B22] cursor-pointer transition-all truncate"
                        [title]="
                          language === 'english' ? sub.label : sub.itaLabel
                        "
                        (click)="
                          selectCategory(i, sub.id); $event.stopPropagation()
                        "
                      >
                        {{ language === 'english' ? sub.label : sub.itaLabel }}
                      </li>
                    </ng-container>
                  </ul>
                </div>
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].lable_3
                }}</label>
                <app-country-picker
                  [control]="getFormControl(row, 'country')"
                ></app-country-picker>
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].lable_4
                }}</label>
                <app-input
                  type="number"
                  [dollar]="false"
                  unit=""
                  placeholder="0.00"
                  [control]="getFormControl(row, 'spend')"
                ></app-input>
                <div
                  class="text-red-500"
                  *ngIf="getFormControl(row, 'spend').errors?.['min']"
                >
                  {{ strings[language].lable_4 }}
                  {{ strings[language].minValidation }}
                </div>
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].lable_5
                }}</label>
                <app-dropdown
                  [options]="currencyOptions"
                  [control]="getFormControl(row, 'currency')"
                ></app-dropdown>
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].lable_6
                }}</label>
                <app-year-picker
                  [control]="getFormControl(row, 'period')"
                ></app-year-picker>
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].lable_7
                }}</label>
                <app-input
                  [dollar]="false"
                  unit=""
                  placeholder="{{ strings[language].placeholder_label2 }}"
                  [control]="getFormControl(row, 'note')"
                ></app-input>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  `
})
export class Category4Question2Component implements OnInit {
  @ViewChild('closeDropdown') dropdownContainer!: ElementRef;
  @ViewChildren('closeDropdown') dropdownContainers!: QueryList<ElementRef>;
  language = inject(LocaleService).getLanguage();
  categoryService = inject(CategoryService);

  form!: FormGroup;
  usage = false;

  currencies = ['EUR', 'USD'];
  currencyOptions = this.currencies.map(c => ({ value: c, label: c }));

  strings: any = {
    english: {
      answer: 'Please fill in the required data if yes to proceed.',
      choice_yes: 'Yes',
      choice_no: 'No',
      add_row: 'Add row',
      remove: 'Remove',
      note_placeholder: 'Optional note',
      do_not_include_title: 'Do not include:',
      exclude_item1: 'Capital assets: machines, vehicles, buildings',
      exclude_item2: 'Energy: fuels, electricity, steam, heat',
      exclude_item3: 'Freight or delivery charges',
      exclude_item4: 'Waste-management fees',
      exclude_item5:
        'Pure services (consulting, software licences, cleaning, insurance, subcontracted labour)',
      price_country_title: 'Price and Country of production:',
      price_note: 'Price to enter = net invoice amount',
      include_discounts: 'Include on-invoice discounts or rebates',
      exclude_vat: 'Exclude VAT and other sales taxes',
      exclude_duties: 'Exclude customs duties',
      exclude_surcharges:
        'Exclude freight, insurance, or other surcharges shown as separate lines',
      country_of_prod_title: 'Country of production:',
      country_known:
        'Choose the country where the product was made if you know it.',
      country_unknown:
        'If you don’t know, use the country where you acquired it.',
      lable_1: 'Purchase category',
      lable_3: 'Country of production',
      lable_4: 'Net Spend',
      lable_5: 'Currency',
      lable_6: 'Period',
      lable_7: 'Note',
      placeholder_label: 'Select purchase category',
      minValidation: 'must be greater than 0',
      capital_goods_note:
        'What belongs in Capital goods' +
        'Include: production machinery, vehicles, buildings & major renovations, HVAC/solar, servers, furniture, lab/medical equipment.' +
        'Exclude: consumables, routine spare parts, energy, freight, pure services and leased assets.',
      placeholder_label2: 'Optional note'
    },
    italian: {
      answer:
        'Si prega di inserire i dati richiesti se la risposta è sì per procedere.',
      choice_yes: 'Sì',
      choice_no: 'No',
      add_row: 'Aggiungi riga',
      remove: 'Rimuovi',
      note_placeholder: 'Nota facoltativa',
      do_not_include_title: 'Non includere:',
      exclude_item1: 'Beni capitali: macchinari, veicoli, edifici',
      exclude_item2: 'Energia: combustibili, elettricità, vapore, calore',
      exclude_item3: 'Costi di trasporto o consegna',
      exclude_item4: 'Spese di gestione dei rifiuti',
      exclude_item5:
        'Servizi puri (consulenza, licenze software, pulizia, assicurazione, manodopera esternalizzata)',
      price_country_title: 'Prezzo e Paese di produzione:',
      price_note: 'Prezzo da inserire = importo netto della fattura',
      include_discounts: 'Includere sconti o rimborsi presenti in fattura',
      exclude_vat: 'Escludere IVA e altre imposte sulle vendite',
      exclude_duties: 'Escludere dazi doganali',
      exclude_surcharges:
        'Escludere spese di trasporto, assicurazione o altri supplementi indicati separatamente',
      country_of_prod_title: 'Paese di produzione:',
      country_known:
        'Scegliere il paese in cui è stato prodotto il bene, se noto.',
      country_unknown:
        'Se non lo si conosce, utilizzare il paese in cui è stato acquisito.',
      lable_1: 'Categoria di acquisto',
      lable_2: 'Sottocategoria',
      lable_3: 'Paese di produzione',
      lable_4: 'Spesa netta',
      lable_5: 'Valuta',
      lable_6: 'Periodo',
      lable_7: 'Nota',
      placeholder_label: 'Seleziona la categoria di acquisto',
      minValidation: 'deve essere maggiore di 0',
      capital_goods_note:
        'Cosa rientra nei Beni capitali' +
        'Includere: macchinari di produzione, veicoli, edifici e grandi ristrutturazioni, HVAC/impianti solari, server, mobili, attrezzature di laboratorio/mediche.' +
        'Escludere: materiali di consumo, parti di ricambio di routine, energia, trasporto, servizi puri e beni in leasing.',
      placeholder_label2: 'Nota facoltativa'
    }
  };

  PRODUCT_CATEGORIES: MainCategory[] = [
    {
      name: 'Construction and Installation Services',
      itaName: 'Servizi di costruzione e installazione in sito',
      subcategories: [
        {
          id: 'construction-type_construction_work',
          label: 'Construction work (general building & civil engineering)',
          itaLabel:
            'Lavori di costruzione (edilizia generale e ingegneria civile)'
        }
      ]
    },
    {
      name: 'Construction and Building Materials (Capital Projects)',
      itaName: 'Materiali per edilizia e lavori civili (progetti capitali)',
      subcategories: [
        {
          id:
            'building_materials-type_bricks_tiles_construction_products_in_baked_clay',
          label: 'Bricks, tiles & other baked-clay construction products',
          itaLabel:
            'Mattoni, tegole e altri prodotti da costruzione in argilla cotta'
        },
        {
          id: 'building_materials-type_cement_lime_plaster',
          label: 'Cement, lime & plaster',
          itaLabel: 'Cemento, calce e gesso'
        },
        {
          id: 'glass_products-type_glass_and_glass_products',
          label: 'Flat glass, bottles & other glass products',
          itaLabel: 'Vetri piani, bottiglie e altri prodotti in vetro'
        },
        {
          id: 'ceramics-type_ceramic_goods',
          label: 'Ceramic goods (sanitary-ware, table-ware, etc.)',
          itaLabel: 'Prodotti ceramici (sanitari, stoviglieria, ecc.)'
        },
        {
          id:
            'timber_forestry-type_products_of_wood_cork_straw_plaiting_materials',
          label: 'Timber, wood, cork, straw & plaiting-material products',
          itaLabel:
            'Prodotti di legno, sughero, paglia e materiali da intreccio'
        },
        {
          id: 'other_materials-type_other_nonmetallic_mineral_products',
          label:
            'Other non-metallic mineral products (e.g., gypsum boards, stone wool)',
          itaLabel:
            'Altri prodotti minerali non metalliferi (es. pannelli in gesso, lana di roccia)'
        },
        {
          id: 'metals-type_basic_iron_steel_of_ferroalloys_first_products',
          label: 'Basic iron & steel, ferro-alloys and first products',
          itaLabel: 'Ferro e acciaio di base, ferroleghe e semilavorati'
        },
        {
          id: 'metals-type_copper_products',
          label: 'Copper & copper products',
          itaLabel: 'Rame e prodotti in rame'
        },
        {
          id: 'metals-type_aluminium_and_aluminium_products',
          label: 'Aluminium & aluminium products',
          itaLabel: 'Alluminio e prodotti in alluminio'
        },
        {
          id: 'metals-type_lead_zinc_tin_products',
          label: 'Lead, zinc & tin products',
          itaLabel: 'Prodotti in piombo, zinco e stagno'
        },
        {
          id: 'metals-type_other_nonferrous_metal_products',
          label: 'Other non-ferrous metal products',
          itaLabel: 'Altri prodotti di metalli non ferrosi'
        },
        {
          id: 'metals-type_precious_metals',
          label: 'Precious metals (gold, silver, platinum, etc.)',
          itaLabel: 'Metalli preziosi (oro, argento, platino, ecc.)'
        },
        {
          id: 'other_materials-type_sand_clay',
          label: 'Bulk sand & clay',
          itaLabel: 'Sabbia e argilla sfuse'
        },
        {
          id: 'chemicals-type_bitumen',
          label: 'Bitumen & asphalt products',
          itaLabel: 'Bitume e prodotti di asfalto'
        }
      ]
    },
    {
      name: 'Machinery and Industrial Equipment',
      itaName: 'Macchinari e attrezzature industriali',
      subcategories: [
        {
          id: 'machinery-type_machinery_equipment_not_elsewhere_specified',
          label: 'Machinery & equipment n.e.c. (not elsewhere classified)',
          itaLabel: 'Macchinari e attrezzature n.c.a.'
        },
        {
          id:
            'electrical_equipment-type_electrical_machinery_apparatus_not_elsewhere_specified',
          label: 'Electrical machinery & apparatus n.e.c.',
          itaLabel: 'Macchinari e apparecchi elettrici n.c.a.'
        }
      ]
    },
    {
      name: 'Electrical, Electronic and Precision Equipment',
      itaName: 'Apparecchiature elettriche, elettroniche e di precisione',
      subcategories: [
        {
          id:
            'electrical_equipment-type_radio_television_communication_equipment_apparatus',
          label: 'Radio, television & telecommunication equipment',
          itaLabel: 'Apparecchiature radio, televisione e telecomunicazioni'
        },
        {
          id:
            'electrical_equipment-type_medical_precision_optical_instruments_watches_clocks',
          label: 'Medical, precision & optical instruments; watches & clocks',
          itaLabel: 'Strumenti medicali, di precisione e ottici; orologi'
        },
        {
          id: 'office_equipment-type_office_machinery_computers',
          label: 'Office machinery & computers',
          itaLabel: 'Macchine per ufficio e computer'
        }
      ]
    },
    {
      name: 'Fabricated Metal Structures and Large Components',
      itaName: 'Strutture metalliche prefabbricate e grandi componenti',
      subcategories: [
        {
          id:
            'metal_products-type_fabricated_metal_products_except_machinery_equipment',
          label: 'Fabricated metal products (excl. machinery & equipment)',
          itaLabel:
            'Prodotti in metallo fabbricati (esclusi macchinari e attrezzature)'
        }
      ]
    },
    {
      name: 'Furniture and Fixtures (Durable)',
      itaName: 'Mobili e arredi (durevoli)',
      subcategories: [
        {
          id:
            'consumer_goods-type_furniture_other_manufactured_goods_not_elsewhere_specified',
          label: 'Furniture & other manufactured consumer goods n.e.c.',
          itaLabel: 'Mobili e altri beni di consumo manifatturieri n.c.a.'
        }
      ]
    },
    {
      name: 'Vehicles and Other Transport Equipment',
      itaName: 'Veicoli e altri mezzi di trasporto',
      subcategories: [
        {
          id:
            'passenger_vehicle-vehicle_type_motor_vehicles_trailers_semitrailers-fuel_source_na-engine_size_na-vehicle_age_na-vehicle_weight_na',
          label: 'Motor vehicles, trailers & semi-trailers (all fuel types)',
          itaLabel:
            'Autoveicoli, rimorchi e semirimorchi (tutti i tipi di alimentazione)'
        },
        {
          id:
            'passenger_vehicle-vehicle_type_other_transport_equipment-fuel_source_na-engine_size_na-vehicle_age_na-vehicle_weight_na',
          label:
            'Other transport equipment (ships, aircraft, rail stock, etc.)',
          itaLabel:
            'Altre attrezzature di trasporto (navi, aeromobili, materiale rotabile, ecc.)'
        }
      ]
    }
  ];
  dropdownOpenStates: boolean[] = [];
  constructor(private fb: FormBuilder) {}
  // Inside your Angular Component:

  subcategoryOptions: any[][] = [];
  categorySearchControl = new FormControl('');
  filteredCategories: any[] = [];
  ngOnInit(): void {
    this.form = this.fb.group({
      purchases: this.fb.array([])
    });
    this.filteredCategories = [...this.PRODUCT_CATEGORIES];

    // Setup search filter
    this.categorySearchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((searchTerm: any) => {
        this.filterCategories(searchTerm);
      });

    const categoriesRaw = localStorage.getItem('categories');
    let categories: Category[] = [];

    if (categoriesRaw) {
      try {
        categories = JSON.parse(categoriesRaw) as Category[];

        while (categories.length < 4) {
          categories.push(this.createDefaultCategory());
        }

        if (!categories[3].questions) categories[3].questions = [];
        if (categories[3].questions.length === 0) {
          categories[3].questions.push(this.createDefaultQuestion());
        }

        const answers = categories[3].questions[1].answers;
        if (answers && Array.isArray(answers)) {
          this.usage = true;
          answers.forEach((ans, index) => {
            const group = this.fb.group({
              mainCategory: [ans.main_category || '', Validators.required],
              country: [ans.spend_region || '', Validators.required],
              spend: [
                ans.money_amount || '',
                [Validators.required, Validators.min(0.01)]
              ],
              currency: [ans.money_unit || 'EUR', Validators.required],
              period: [ans.spend_year || '', Validators.required],
              note: [ans.note || '']
            });

            this.purchases.push(group);
            this.dropdownOpenStates.push(false); // Initialize dropdown state for this row
          });
        }
      } catch (e) {
        console.error('Error parsing localStorage categories:', e);
      }
    }

    // Initialize dropdown states array if empty
    if (this.dropdownOpenStates.length === 0 && this.purchases.length > 0) {
      this.dropdownOpenStates = new Array(this.purchases.length).fill(false);
    }

    this.purchases.valueChanges.subscribe(() => {
      this.saveToLocalStorage();
    });
  }

  get purchases(): FormArray {
    return this.form.get('purchases') as FormArray;
  }

  addRow(): void {
    const group = this.fb.group({
      mainCategory: ['', Validators.required],
      country: ['', Validators.required],
      spend: ['', [Validators.required, Validators.min(0.01)]],
      currency: ['EUR', Validators.required],
      period: ['', Validators.required],
      note: ['']
    });
    this.purchases.push(group);
    this.dropdownOpenStates.push(false);
  }

  removeRow(index: number): void {
    this.purchases.removeAt(index);
    this.dropdownOpenStates.splice(index, 1); // Remove corresponding state
  }

  toggleUsage(value: boolean): void {
    this.usage = value;
    if (value && this.purchases.length === 0) {
      this.addRow();
    }
    if (!value) {
      this.purchases.clear();
      this.subcategoryOptions = [];
      this.saveToLocalStorage();
    }
  }

  getFormControl(row: AbstractControl, controlName: string): FormControl {
    return row.get(controlName) as FormControl;
  }

  private _mainCategoriesCache: { lang: string; opts: { value: string; label: string }[] } | null = null;

  get exiobaseMainCategories() {
    const lang = this.language;
    if (this._mainCategoriesCache?.lang === lang) {
      return this._mainCategoriesCache.opts;
    }
    const opts = this.PRODUCT_CATEGORIES.map(x => ({
      value: x.name,
      label: lang === 'english' ? x.name : x.itaName
    }));
    this._mainCategoriesCache = { lang, opts };
    return opts;
  }

  saveToLocalStorage(): void {
    const purchasesData: any[] = this.purchases.controls.map(group => {
      const g = group.value;
      const selectedId = g.mainCategory;

      let mainCategoryInfo = null;
      let subcategoryInfo: Subcategory | null = null;

      for (const category of this.PRODUCT_CATEGORIES) {
        const foundSub = category.subcategories.find(
          sub => sub.id === selectedId
        );
        if (foundSub) {
          mainCategoryInfo = {
            name: category.name,
            itaName: category.itaName
          };
          subcategoryInfo = foundSub;
          break;
        }
      }

      return {
        main_category: g.mainCategory,
        main_category_info: mainCategoryInfo,
        purchase_category_info: subcategoryInfo,
        spend_region: g.country,
        money_amount: g.spend,
        money_unit: g.currency,
        spend_year: g.period,
        note: g.note
      };
    });

    let categories =
      ((this.categoryService.categories() as unknown) as Category[]) || [];

    while (categories.length < 4) {
      categories.push(this.createDefaultCategory());
    }

    if (!categories[3].questions) categories[3].questions = [];
    if (categories[3].questions.length === 0) {
      categories[3].questions.push(this.createDefaultQuestion());
    }

    categories[3].questions[1].answers = this.usage ? purchasesData : null;
    this.categoryService.persistCategories(categories);
  }

  private createDefaultCategory(): Category {
    return {
      id: '',
      done: false,
      title: '',
      heading: '',
      doubt: '',
      emissions: [],
      questions: [this.createDefaultQuestion()]
    };
  }

  private createDefaultQuestion(): Question {
    return {
      id: '',
      done: false,
      title: '',
      heading: '',
      doubt: '',
      emissions: [],
      answers: null
    };
  }
  dropdownOpenRow: any = null;

  toggleDropdown(index: number) {
    // Toggle current row and close others
    this.dropdownOpenStates = this.dropdownOpenStates.map((state, i) =>
      i === index ? !state : false
    );
  }

  selectCategory(index: number, subcategoryId: string) {
    const row = this.purchases.at(index);
    this.getFormControl(row, 'mainCategory').setValue(subcategoryId);
    this.dropdownOpenStates[index] = false; // Close after selection
  }
  getSelectedLabel(row: any): string | null {
    const selectedId = this.getFormControl(row, 'mainCategory').value;
    for (const category of this.PRODUCT_CATEGORIES) {
      const found = category.subcategories.find(sub => sub.id === selectedId);
      if (found) {
        const label =
          this.language === 'english' ? found.label : found.itaLabel;
        return label ?? null;
      }
    }
    return null;
  }
  filterCategories(searchTerm: string) {
    if (!searchTerm) {
      this.filteredCategories = [...this.PRODUCT_CATEGORIES];
      return;
    }

    const term = searchTerm.toLowerCase();
    this.filteredCategories = this.PRODUCT_CATEGORIES.map(category => {
      // Filter subcategories first
      const filteredSubs = category.subcategories.filter(
        sub =>
          sub.label.toLowerCase().includes(term) ||
          sub.id.toLowerCase().includes(term)
      );

      // Only include category if it matches search or has matching subcategories
      if (
        category.name.toLowerCase().includes(term) ||
        filteredSubs.length > 0
      ) {
        return {
          ...category,
          subcategories: filteredSubs
        };
      }
      return null;
    }).filter(category => category !== null);
  }
  @HostListener('document:click', ['$event'])
  handleClickOutside(event: MouseEvent) {
    // Check if click was inside any dropdown container
    const clickedInside = this.dropdownContainers.some(container =>
      container.nativeElement.contains(event.target)
    );

    if (!clickedInside) {
      this.dropdownOpenStates = this.dropdownOpenStates.map(() => false);
    }
  }
}
